
import imageCompression from 'browser-image-compression';

/**
 * Converts an image to WebP format with compression
 * @param file Original image file
 * @returns Promise resolving to the converted WebP file
 */
export const convertToWebP = async (file: File): Promise<File> => {
  try {
    // Skip if already webp
    if (file.type === 'image/webp') {
      console.log('Image is already in WebP format, skipping conversion');
      return file;
    }

    console.log('Starting WebP conversion for:', file.name);
    
    // First compress the image using browser-image-compression
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 2560,
      useWebWorker: true,
      fileType: 'image/webp', // Set output to WebP
      initialQuality: 0.8
    };
    
    // Perform the compression/conversion
    const webpFile = await imageCompression(file, options);
    
    // Create a new File with proper naming
    const fileName = file.name.replace(/\.(jpe?g|png|gif|bmp)$/i, '.webp');
    const convertedFile = new File([webpFile], fileName, { 
      type: 'image/webp',
      lastModified: Date.now() 
    });
    
    console.log(`Converted ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB) to WebP: ${convertedFile.name} (${(convertedFile.size / (1024 * 1024)).toFixed(2)}MB)`);
    
    return convertedFile;
  } catch (error) {
    console.error('Error converting image to WebP:', error);
    return file; // Return original if conversion fails
  }
};

/**
 * Safely checks if an image URL is a WebP image
 * @param url Image URL to check
 * @returns boolean indicating if the image is WebP
 */
export const isWebPImage = (url: string): boolean => {
  try {
    if (!url) return false;
    return url.toLowerCase().endsWith('.webp');
  } catch (error) {
    return false;
  }
};

/**
 * Converts an image URL to a WebP URL if not already WebP
 * For already stored images, this appends a WebP transformation parameter
 * @param url Original image URL
 * @returns URL converted to request WebP format
 */
export const convertUrlToWebP = (url: string | null): string => {
  if (!url) return '/placeholder.svg';
  
  try {
    // Skip if already webp
    if (isWebPImage(url)) {
      return url;
    }

    // Handle Supabase storage URLs
    if (url.includes('supabase.co/storage')) {
      // Extract URL parts
      const urlObj = new URL(url);
      
      // Add format=webp parameter
      urlObj.searchParams.set('format', 'webp');
      return urlObj.toString();
    }
    
    // For external URLs, we can't convert them directly
    // Just return the original URL
    return url;
  } catch (error) {
    console.error('Error converting URL to WebP:', error);
    return url;
  }
};
